import { useEffect, useId, useRef, useState } from 'react'
import GlassOverlay from './GlassOverlay'
import ScanGlassSelect from './scan/ScanGlassSelect'
import { classifyImageUploadError } from '../utils/imageUploadErrors'
import { buildEditItemPayload } from '../utils/editProductOutcome'
import { readEditPricing } from '../utils/japanPrice'
import { blurNumberOnWheel } from '../utils/numberInputWheel'
import { apiUrl } from '../utils/apiBase'
import { exchangeRatesService } from '../services/api'
import { formatCoordinates, requestCurrentPosition } from '../services/scan/geolocation'
import { resolveJpyEurRate } from '../services/scan/exchangeRateCache'
import { readItemStore } from '../utils/itemMeta'
import { IconPin } from './scan/ScanIcons'
import './AddProductModal.css'
import './EditProductModal.css'
import './scan/ScanProductFlow.css'

const CURRENCY_OPTIONS = [
  { value: 'JPY', label: 'JPY · ¥' },
  { value: 'EUR', label: 'EUR · €' },
]

function CloseGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M5 12.5l5 5L19 7.5" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ReplaceImageGlyph() {
  return (
    <span className="add-product-compound-icon">
      <img src="/Icon photo size select actual.png" alt="" />
      <img src="/Icon restart alt.png" alt="" className="add-product-compound-badge" />
    </span>
  )
}

function IconButton({
  type = 'button',
  icon,
  label,
  onClick,
  disabled = false,
  busy = false,
  motion = 'lift',
  primary = false,
  glyph = null,
}) {
  return (
    <button
      type={type}
      className={`add-product-icon-btn is-${motion}${primary ? ' is-primary' : ''} ${busy ? ' is-busy' : ''} is-plain`}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-busy={busy || undefined}
    >
      {glyph || <img src={icon} alt="" />}
    </button>
  )
}

function toDraft(item) {
  const pricing = readEditPricing(item)
  const store = readItemStore(item)
  return {
    name: item?.name || '',
    link: item?.link || '',
    price: pricing.amount === '' ? '' : pricing.amount,
    currency: pricing.currency,
    quantity: item?.quantity || 1,
    promo: item?.promo || '',
    image_url: item?.image_url || '',
    image_key: item?.image_key || '',
    convertedEur: pricing.convertedEur,
    store,
    storeDirty: false,
  }
}

export default function EditProductModal({
  isOpen,
  item,
  onClose,
  onSubmit,
  isSubmitting = false,
  submitError = '',
  returnFocusRef,
  t = (key) => key,
}) {
  const titleId = useId()
  const hintId = useId()
  const nameInputRef = useRef(null)
  const [draft, setDraft] = useState(() => toDraft(item))
  const [errors, setErrors] = useState({})
  const [pastingImage, setPastingImage] = useState(false)
  const [imageReady, setImageReady] = useState(false)
  const [localPreview, setLocalPreview] = useState('')
  const [locating, setLocating] = useState(false)
  const [locationError, setLocationError] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined
    setDraft(toDraft(item))
    setImageReady(Boolean(item?.image_url))
    setLocalPreview('')
    setErrors({})
    setLocationError('')
    setLocating(false)
    return undefined
  }, [isOpen, item?.id])

  const updateField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  const validate = () => {
    const next = {}
    if (!draft.name.trim()) next.name = 'Indiquez un nom de produit.'
    if (draft.link.trim()) {
      try {
        const parsed = new URL(draft.link.trim())
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          next.link = 'L’URL doit commencer par http:// ou https://.'
        }
      } catch {
        next.link = 'URL invalide.'
      }
    }
    if (Number(draft.price) < 0) next.price = 'Le prix ne peut pas être négatif.'
    if (draft.currency === 'JPY' && !(Number(draft.price) > 0)) next.price = 'Indiquez le prix constaté en yens.'
    if (!draft.quantity || draft.quantity < 1) next.quantity = 'La quantité doit être au moins 1.'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handlePasteImage = async () => {
    if (pastingImage || isSubmitting) return
    setPastingImage(true)
    setErrors((prev) => ({ ...prev, image_url: '' }))
    let previewUrl = ''
    try {
      const clipboardItems = await navigator.clipboard.read()
      for (const clipboardItem of clipboardItems) {
        const imageType = clipboardItem.types.find((type) => type.startsWith('image/'))
        if (!imageType) continue
        const blob = await clipboardItem.getType(imageType)
        previewUrl = URL.createObjectURL(blob)
        setLocalPreview(previewUrl)
        const file = new File([blob], 'pasted-image.png', { type: blob.type })
        const formData = new FormData()
        formData.append('file', file)
        const response = await fetch(apiUrl('/api/upload/image'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          body: formData,
        })
        if (!response.ok) {
          let detail = ''
          try {
            detail = (await response.json())?.detail || ''
          } catch {
            detail = ''
          }
          throw Object.assign(new Error(detail || 'Le transfert de l’image a échoué.'), { status: response.status })
        }
        const result = await response.json()
        if (!result?.url || !result?.key) {
          throw Object.assign(new Error('Le stockage n’a pas confirmé l’enregistrement.'), { status: 502 })
        }
        setDraft((prev) => ({ ...prev, image_url: result.url, image_key: result.key }))
        setImageReady(false)
        setLocalPreview('')
        URL.revokeObjectURL(previewUrl)
        setPastingImage(false)
        return
      }
      setErrors((prev) => ({ ...prev, image_url: 'Aucune image dans le presse-papier.' }))
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setLocalPreview('')
      }
      setErrors((prev) => ({
        ...prev,
        image_url: classifyImageUploadError(error, { status: error?.status }),
      }))
    } finally {
      setPastingImage(false)
    }
  }

  const updateStore = (partial) => {
    setDraft((prev) => ({
      ...prev,
      store: prev.store ? { ...prev.store, ...partial } : { ...partial },
      storeDirty: true,
    }))
    setLocationError('')
  }

  const saveCurrentPosition = async () => {
    if (locating || isSubmitting || pastingImage) return
    setLocating(true)
    setLocationError('')
    try {
      const position = await requestCurrentPosition()
      setDraft((prev) => ({
        ...prev,
        storeDirty: true,
        store: {
          ...(prev.store || {}),
          lat: position.lat,
          lng: position.lng,
          accuracy: position.accuracy,
          recordedAt: position.recordedAt || new Date().toISOString(),
          placeId: '',
          provider: 'gps',
          mode: 'coords-only',
        },
      }))
    } catch (error) {
      const denied = String(error?.message || '').includes('denied')
      setLocationError(denied ? 'Localisation refusée. Saisie manuelle disponible.' : 'Position introuvable. Saisie manuelle disponible.')
    } finally {
      setLocating(false)
    }
  }

  const handleRemoveImage = () => {
    setDraft((prev) => ({ ...prev, image_url: '', image_key: '' }))
    setLocalPreview('')
    setImageReady(false)
  }

  const changeCurrency = (nextCurrency) => {
    if (nextCurrency === draft.currency) return
    setDraft((prev) => ({
      ...prev,
      currency: nextCurrency,
      price: '',
      convertedEur: null,
    }))
    setErrors((prev) => ({ ...prev, price: '' }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isSubmitting || pastingImage || locating) return
    if (!validate()) return
    const conversion = draft.currency === 'JPY'
      ? await resolveJpyEurRate(() => exchangeRatesService.jpyToEur())
      : null
    onSubmit(buildEditItemPayload(draft, { item, conversion }))
  }

  const busy = isSubmitting || pastingImage || locating

  return (
    <GlassOverlay
      isOpen={isOpen}
      onClose={onClose}
      allowClose={!busy}
      labelledBy={titleId}
      describedBy={hintId}
      panelClassName="add-product-dialog edit-product-dialog"
      initialFocusRef={nameInputRef}
      returnFocusRef={returnFocusRef}
    >
      <div className="add-product-content">
        <header className="add-product-header">
          <div>
            <h2 id={titleId}>{t('editProduct') || 'Modifier le produit'}</h2>
            <p id={hintId} className="add-product-hint">
              Ajustez les informations du produit, puis enregistrez.
            </p>
          </div>
          <IconButton
            label="Fermer"
            onClick={onClose}
            disabled={busy}
            motion="spin"
            glyph={<CloseGlyph />}
          />
        </header>

        <form className="add-product-form" onSubmit={handleSubmit} noValidate aria-busy={busy || undefined}>
          <div className="add-product-body">
            <div className={`add-product-field${errors.name ? ' has-error' : ''}`}>
              <label htmlFor="edit-product-name">{t('productName') || 'Nom'} *</label>
              <input
                id="edit-product-name"
                ref={nameInputRef}
                type="text"
                value={draft.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
              {errors.name ? <p className="add-product-error">{errors.name}</p> : null}
            </div>

            <div className={`add-product-field${errors.link ? ' has-error' : ''}`}>
              <label htmlFor="edit-product-url">{t('linkUrl') || 'URL'}</label>
              <input
                id="edit-product-url"
                type="url"
                inputMode="url"
                value={draft.link}
                onChange={(e) => updateField('link', e.target.value)}
                placeholder="https://..."
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
              />
              {errors.link ? <p className="add-product-error">{errors.link}</p> : null}
            </div>

            <div className="add-product-row">
              <div className={`add-product-field${errors.price ? ' has-error' : ''}`}>
                <label htmlFor="edit-product-price">
                  {draft.currency === 'JPY' ? 'Prix constaté (JPY)' : 'Prix constaté (EUR)'}
                </label>
                <div className="edit-price-input">
                  <span aria-hidden="true">{draft.currency === 'JPY' ? '¥' : '€'}</span>
                  <input
                    id="edit-product-price"
                    type="number"
                    inputMode="decimal"
                    step={draft.currency === 'JPY' ? '1' : '0.01'}
                    min="0"
                    value={draft.price}
                    onChange={(e) => updateField('price', e.target.value)}
                    onWheel={blurNumberOnWheel}
                  />
                </div>
                {draft.currency === 'JPY' && Number(draft.convertedEur) > 0 ? (
                  <p className="add-product-hint">
                    Conversion indicative : {Number(draft.convertedEur).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                  </p>
                ) : null}
                {errors.price ? <p className="add-product-error">{errors.price}</p> : null}
              </div>
              <div className={`add-product-field${errors.quantity ? ' has-error' : ''}`}>
                <label htmlFor="edit-product-qty">{t('quantity') || 'Quantité'}</label>
                <input
                  id="edit-product-qty"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={draft.quantity}
                  onChange={(e) => updateField('quantity', parseInt(e.target.value, 10) || 1)}
                  onWheel={blurNumberOnWheel}
                />
                {errors.quantity ? <p className="add-product-error">{errors.quantity}</p> : null}
              </div>
            </div>

            <div className="add-product-field">
              <label htmlFor="edit-product-currency">Devise</label>
              <ScanGlassSelect
                id="edit-product-currency"
                label="Devise"
                value={draft.currency === 'JPY' ? 'JPY' : 'EUR'}
                options={CURRENCY_OPTIONS}
                onChange={changeCurrency}
                disabled={busy}
              />
            </div>

            <div className="add-product-field">
              <label htmlFor="edit-product-promo">{t('promoLabel') || 'Promotion'}</label>
              <input
                id="edit-product-promo"
                type="text"
                value={draft.promo}
                onChange={(e) => updateField('promo', e.target.value)}
              />
            </div>

            <div className={`add-product-field add-product-image-field${errors.image_url ? ' has-error' : ''}`}>
              <span className="add-product-label">{t('image') || 'Image'}</span>
              <div className="add-product-image-box">
                <div className={`add-product-image-preview${(localPreview || (draft.image_url && imageReady)) ? ' is-ready' : ''}${localPreview ? ' is-local' : ''}`}>
                  {localPreview || draft.image_url ? (
                    <img
                      src={localPreview || draft.image_url}
                      alt={localPreview ? 'Aperçu local, pas encore enregistré' : 'Aperçu du produit'}
                      onLoad={() => setImageReady(true)}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        setErrors((prev) => ({
                          ...prev,
                          image_url: 'L’image n’a pas pu être chargée. Collez-en une autre ou retirez-la.',
                        }))
                      }}
                    />
                  ) : (
                    <img src="/Icon photo size select actual.png" alt="" className="add-product-image-empty" />
                  )}
                </div>
                <div className="add-product-image-actions">
                  <IconButton
                    icon="/Icon paste.png"
                    label="Coller l’image"
                    onClick={handlePasteImage}
                    disabled={busy}
                    busy={pastingImage}
                    motion="lift"
                  />
                  <IconButton
                    label="Remplacer l’image"
                    onClick={handlePasteImage}
                    disabled={busy || !draft.image_url}
                    motion="spin"
                    glyph={<ReplaceImageGlyph />}
                  />
                  <IconButton
                    icon="/Icon trash.png"
                    label="Retirer l’image"
                    onClick={handleRemoveImage}
                    disabled={busy || !draft.image_url}
                    motion="tilt"
                  />
                  <input
                    type="url"
                    value={draft.image_url}
                    onChange={(e) => {
                      updateField('image_url', e.target.value)
                      setImageReady(false)
                    }}
                    placeholder="URL de l’image"
                    aria-label="URL de l’image"
                    inputMode="url"
                    autoCapitalize="none"
                    spellCheck={false}
                  />
                </div>
              </div>
              {errors.image_url ? <p className="add-product-error">{errors.image_url}</p> : null}
            </div>

            <div className="add-product-field edit-product-location">
              <span className="add-product-label">Emplacement</span>
              {draft.store ? (
                <div className="scan-store-summary">
                  <div>
                    <strong>{draft.store.name || 'Position enregistrée'}</strong>
                    {draft.store.address ? <p>{draft.store.address}</p> : null}
                    {draft.store.lat != null && draft.store.lng != null ? (
                      <small>{formatCoordinates(draft.store)}</small>
                    ) : null}
                  </div>
                </div>
              ) : (
                <p className="add-product-hint">Aucun emplacement associé. Nom et adresse restent facultatifs.</p>
              )}
              <input
                id="edit-product-store-name"
                type="text"
                value={draft.store?.name || ''}
                onChange={(event) => updateStore({ name: event.target.value })}
                placeholder="Nom du magasin"
              />
              <textarea
                id="edit-product-store-address"
                rows={2}
                value={draft.store?.address || ''}
                onChange={(event) => updateStore({ address: event.target.value })}
                placeholder="Adresse complète"
              />
              <div className="edit-product-location-actions">
                <button
                  type="button"
                  className="scan-store-save-position"
                  onClick={saveCurrentPosition}
                  disabled={locating || busy}
                  aria-label="Enregistrer les coordonnées géographiques actuelles"
                >
                  <IconPin />
                  <span>{locating ? 'Localisation…' : 'Enregistrer les coordonnées géographiques actuelles'}</span>
                </button>
                {draft.store ? (
                  <button
                    type="button"
                    className="add-product-text-btn"
                    onClick={() => {
                      setDraft((prev) => ({ ...prev, store: null, storeDirty: true }))
                      setLocationError('')
                    }}
                  >
                    Retirer l’emplacement
                  </button>
                ) : null}
              </div>
              {locationError ? <p className="add-product-error" role="status">{locationError}</p> : null}
            </div>
            {submitError ? <p className="add-product-error add-product-submit-error">{submitError}</p> : null}
          </div>

          <footer className="add-product-footer">
            <IconButton
              label="Annuler"
              onClick={onClose}
              disabled={isSubmitting}
              motion="tilt"
              glyph={<CloseGlyph />}
            />
            <IconButton
              type="submit"
              label={isSubmitting ? 'Enregistrement en cours' : 'Enregistrer'}
              disabled={busy}
              busy={isSubmitting}
              motion="pop"
              primary
              glyph={<CheckGlyph />}
            />
          </footer>
        </form>
      </div>
    </GlassOverlay>
  )
}
